import { loadStripe, type Stripe } from '@stripe/stripe-js';

/**
 * Stripe.js loader, memoized per publishable key. We initialize from the
 * publishable key returned by our stripe-setup-intent endpoint (rather than a
 * build-time env inline) so the client always uses whatever key the server is
 * configured with — test vs live can't drift between client and server.
 */
const cache = new Map<string, Promise<Stripe | null>>();

export function stripePromiseFor(publishableKey: string): Promise<Stripe | null> {
  if (!cache.has(publishableKey)) {
    cache.set(publishableKey, loadStripe(publishableKey));
  }
  return cache.get(publishableKey)!;
}

export interface StripeMethod {
  stripe_pm_id: string;
  kind: 'card' | 'ach';
  brand: string | null;
  last4: string | null;
  exp_month: number | null;
  exp_year: number | null;
  is_default: boolean;
  is_autopay: boolean;
}

/**
 * A bank whose routing/account numbers were typed in, still waiting on
 * Stripe's microdeposit to arrive. It is NOT in `methods` — it cannot be
 * charged, put on autopay or made main payer until it is verified.
 */
export interface PendingStripeMethod {
  stripe_pm_id: string;
  kind: 'card' | 'ach';
  brand: string | null;
  last4: string | null;
  verification_url: string | null;
  microdeposits_arrive_on: string | null;
}

/** "Visa ···4242 · 12/29" / "Wells Fargo ···6789" */
export function stripeMethodLabel(m: StripeMethod | PendingStripeMethod): string {
  const name = m.brand ?? (m.kind === 'ach' ? 'Bank account' : 'Card');
  const tail = m.last4 ? ` ···${m.last4}` : '';
  const expMonth = 'exp_month' in m ? m.exp_month : null;
  const expYear = 'exp_year' in m ? m.exp_year : null;
  const exp =
    m.kind === 'card' && expMonth && expYear
      ? ` · ${String(expMonth).padStart(2, '0')}/${String(expYear).slice(-2)}`
      : '';
  return `${name}${tail}${exp}`;
}
